import {useState} from 'react';
import {ChatCircleDots, PaperPlaneRight, Sparkle} from '@phosphor-icons/react';
import {VStack} from '@astryxdesign/core/VStack';
import {HStack} from '@astryxdesign/core/HStack';
import {Text} from '@astryxdesign/core/Text';
import {Button} from '@astryxdesign/core/Button';
import {Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerClose} from '@/components/ui/drawer';
import {useAssistantChat} from '@/hooks/useAssistantChat';
import {useTenantContext} from '@/app/TenantContext';
import {ChatBubbles} from './ChatBubbles';
import {CLIENT_SUGGESTIONS, OWNER_SUGGESTIONS} from '@/lib/assistant/router';

export function AssistantSheet({
  slug,
  scope,
  accessToken,
  fab = false,
}: {
  slug: string;
  scope: 'client' | 'owner';
  accessToken?: string;
  fab?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const {tenant, services} = useTenantContext();
  const chat = useAssistantChat(slug, scope, {tenant, services}, accessToken);
  const suggestions = scope === 'owner' ? OWNER_SUGGESTIONS : CLIENT_SUGGESTIONS;

  function send(text: string) {
    const value = text.trim();
    if (!value) return;
    setInput('');
    void chat.send(value);
  }

  return (
    <>
      {fab ? (
        <button
          type="button"
          aria-label="Помощник студии"
          onClick={() => setOpen(true)}
          className="nav-pill fixed bottom-[calc(var(--bottom-nav-height,96px)+env(safe-area-inset-bottom,0px))] right-4 z-40 flex h-14 w-14 items-center justify-center gap-2 rounded-full text-foreground lg:bottom-6 lg:right-8 lg:h-[52px] lg:w-auto lg:px-5"
        >
          <Sparkle size={22} weight="fill" aria-hidden />
          <span className="hidden text-[15px] font-semibold lg:inline">Помощник</span>
        </button>
      ) : (
        <Button
          label="Помощник"
          variant="secondary"
          size="sm"
          icon={<ChatCircleDots size={16} />}
          onClick={() => setOpen(true)}
        />
      )}
      <Drawer open={open} onOpenChange={setOpen} swipeDirection="down">
        <DrawerContent>
          <DrawerHeader>
            <HStack hAlign="between" vAlign="center">
              <DrawerTitle>Помощник студии</DrawerTitle>
              <DrawerClose className="rounded-md px-2 py-1 text-sm text-muted-foreground hover:text-foreground">Закрыть</DrawerClose>
            </HStack>
          </DrawerHeader>
          <VStack gap={3}>
            {chat.messages.length === 0 ? (
              <VStack gap={2}>
                <Text type="supporting">Спросите обычным языком — например, «Хочу мойку завтра после 6».</Text>
                <HStack gap={2} wrap="wrap">
                  {suggestions.map((s) => (
                    <Button key={s} label={s} variant="secondary" size="sm" onClick={() => send(s)} />
                  ))}
                </HStack>
              </VStack>
            ) : null}
            <ChatBubbles slug={slug} messages={chat.messages} pending={chat.isPending} onChoice={send} />
            <HStack gap={2} vAlign="center">
              <input
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-foreground"
                placeholder="Ваш вопрос" aria-label="Ваш вопрос"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') send(input);
                }}
              />
              <Button
                label="Отправить"
                isIconOnly
                variant="primary"
                icon={<PaperPlaneRight size={16} />}
                isLoading={chat.isPending}
                onClick={() => send(input)}
              />
            </HStack>
          </VStack>
        </DrawerContent>
      </Drawer>
    </>
  );
}
